import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import TransactionStatusCard from './components/TransactionStatusCard';
import TransactionDetailsCard from './components/TransactionDetailsCard';
import UserInfoCard from './components/UserInfoCard';
import ActionButtonsSection from './components/ActionButtonsSection';
import NotificationConfirmation from './components/NotificationConfirmation';
import Icon from '../../components/AppIcon';

const PaymentConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get data from location state or simulate transaction data
    const locationData = location?.state;
    
    if (locationData) {
      setTransactionData(locationData?.transactionData);
      setUserData(locationData?.userData);
      setStatus(locationData?.status || 'pending');
    } else {
      // Mock data for direct access
      const mockTransactionData = {
        transactionId: 'AF-2024-001234',
        referenceNumber: '789456',
        paymentMethod: 'Pago Móvil',
        bankIssuer: 'Banco de Venezuela',
        amount: 50.00,
        quantity: 1,
        timestamp: new Date('2024-11-04T01:15:00'),
        receiptUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
        ticketNumbers: ['AF-2024-001234']
      };

      const mockUserData = {
        fullName: 'María Alejandra González',
        cedula: 'V-12.345.678',
        email: 'maria.gonzalez@email.com',
        phone: '+58 412-123-4567'
      };

      setTransactionData(mockTransactionData);
      setUserData(mockUserData);
      setStatus('verified'); // Change to 'pending' or 'failed' to test different states
    }

    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [location?.state]);

  useEffect(() => {
    // Simulate status updates for pending transactions
    if (status === 'pending') {
      const statusTimer = setTimeout(() => {
        setStatus('verified');
        setTransactionData(prev => ({
          ...prev,
          ticketNumbers: ['AF-2024-001234']
        }));
      }, 30000); // Update after 30 seconds

      return () => clearTimeout(statusTimer);
    }
  }, [status]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="RefreshCw" size={32} className="text-secondary animate-spin" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Procesando Confirmación
            </h2>
            <p className="text-muted-foreground font-body">
              Verificando los detalles de tu transacción...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!transactionData || !userData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-error" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Información No Encontrada
            </h2>
            <p className="text-muted-foreground font-body mb-6">
              No se encontraron datos de la transacción. Por favor, intenta nuevamente desde el proceso de compra.
            </p>
            <button
              onClick={() => navigate('/purchase-flow')}
              className="bg-secondary text-secondary-foreground px-6 py-3 rounded-lg font-body font-medium hover:bg-secondary/90 transition-premium"
            >
              Ir a Comprar Boletos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Confirmación de Pago
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
              Aquí tienes todos los detalles de tu transacción y el estado actual de tu compra
            </p>
          </div>

          {/* Transaction Status */}
          <div className="mb-8">
            <TransactionStatusCard 
              status={status} 
              transactionData={transactionData}
            />
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Transaction Details */}
            <div className="space-y-6">
              <TransactionDetailsCard transactionData={transactionData} />
              
              {/* Notification Confirmation - Only show for verified transactions */}
              {status === 'verified' && (
                <NotificationConfirmation 
                  userData={userData} 
                  transactionData={transactionData}
                />
              )}
            </div>

            {/* User Information */}
            <div>
              <UserInfoCard userData={userData} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mb-8">
            <ActionButtonsSection 
              status={status} 
              transactionData={transactionData}
            />
          </div>

          {/* Additional Information */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={20} className="text-secondary" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-card-foreground mb-2">
                  Información Importante
                </h3>
                <ul className="text-sm text-muted-foreground space-y-2 font-caption">
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Guarda este comprobante para futuras referencias</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Los boletos se asignan automáticamente tras la verificación del pago</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Recibirás notificaciones sobre el estado del sorteo</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Puedes verificar tus boletos en cualquier momento usando tu cédula</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => {
            const message = `Hola, tengo una consulta sobre mi transacción ${transactionData?.referenceNumber}`;
            const whatsappUrl = `https://wa.me/584121234567?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
          }}
          className="w-14 h-14 bg-success hover:bg-success/90 text-white rounded-full shadow-luxury flex items-center justify-center transition-premium hover:scale-105"
        >
          <Icon name="MessageCircle" size={24} />
        </button>
      </div>
    </div>
  );
};

export default PaymentConfirmation;